import { useEffect } from 'react';
import { getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import { invariant } from '@e-socna/utils';
import { EMPTY_OBJECT, EMPTY_STRING, TEMPORARY_STRING } from '@e-socna/empty';

import { LazyEditor } from '../../../../components/Editor/Lazy';
import { useContextualizedFields } from '../../../../hooks/useContextualizedFields.hook';
import { useFormikOnNextTick } from '../../../../hooks/useFormikOnNextTick.hook';
import { getTouchedError } from '../../../../utils/get-touched-error.util';
import type { DocumentResolversSettings } from '../../../../components/Editor/types/document-resolvers-settings.type';
import type { FileStorage } from '../../../../components/Editor/types/file-storage.type';

import type { ResolvableServiceForm_ResolvableServiceBaseInterface } from '../../fragments/resolvable-service-base-interface.fragment';

type ContextualizedFields = Readonly<
  Required<
    Pick<
      ResolvableServiceForm_ResolvableServiceBaseInterface,
      'codingConfig' | 'content' | '_id' | 'label' | 'title'
    >
  >
>;

interface Props {
  readonly documentResolversSettings: DocumentResolversSettings;
  readonly formContext?: string;
  readonly id: string;
  readonly storage: FileStorage;
}

export const ResolvableServiceInfoFormStep = ({
  documentResolversSettings,
  formContext,
  storage,
}: Readonly<Props>) => {
  const {
    errors,
    handleBlur,
    handleChange,
    initialValues,
    isSubmitting,
    touched,
  } = useFormikContext<ResolvableServiceForm_ResolvableServiceBaseInterface>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const fieldsBase = useContextualizedFields<ContextualizedFields>(
    {
      codingConfig: {
        id: 'resolvable-service-coding-config',
        name: 'codingConfig',
      },
      content: {
        id: 'resolvable-service-content',
        name: 'content',
      },
      _id: {
        id: 'resolvable-service-id',
        name: '_id',
      },
      label: {
        id: 'resolvable-service-label',
        name: 'label',
      },
      title: {
        id: 'resolvable-service-title',
        name: 'title',
      },
    },
    formContext,
  );

  invariant(
    'code' in fieldsBase.codingConfig.value,
    'Unexpected codingConfig type',
  );

  const fields = {
    ...fieldsBase,
    codingConfigCode: {
      id: `${fieldsBase.codingConfig.id}-code`,
      name: `${fieldsBase.codingConfig.name}.code`,
      value: fieldsBase.codingConfig.value.code,
    },
  };

  useEffect(() => {
    if (fields.codingConfigCode.value !== TEMPORARY_STRING) {
      return;
    }

    setFieldValueOnNextTick(fields.codingConfigCode.name, EMPTY_STRING);
  }, [
    fields.codingConfigCode.name,
    fields.codingConfigCode.value,
    setFieldValueOnNextTick,
  ]);

  useEffect(() => {
    if (fields.label.value !== TEMPORARY_STRING) {
      return;
    }

    setFieldValueOnNextTick(fields.label.name, EMPTY_STRING);
  }, [fields.label.name, fields.label.value, setFieldValueOnNextTick]);

  const contentError = getTouchedError(errors, touched, fields.content.name);

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          md: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!getTouchedError(errors, touched, fields.codingConfigCode.name)
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            fields.codingConfigCode.name,
          )}
          id={fields.codingConfigCode.id}
          label="Code"
          name={fields.codingConfigCode.name}
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={fields.codingConfigCode.value}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 8,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={!!getTouchedError(errors, touched, fields.label.name)}
          fullWidth
          helperText={getTouchedError(errors, touched, fields.label.name)}
          id={fields.label.id}
          label="Libellé"
          name={fields.label.name}
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={fields.label.value}
          variant="outlined"
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={!!getTouchedError(errors, touched, fields.title.name)}
          fullWidth
          helperText={getTouchedError(errors, touched, fields.title.name)}
          id={fields.title.id}
          label="Titre"
          name={fields.title.name}
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          value={fields.title.value}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={12}
        sx={{ display: 'flex', flexDirection: 'column', pt: 1, pb: 1 }}
      >
        <LazyEditor
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(contentError
            ? {
                alert: {
                  severity: 'error',
                  content: contentError,
                },
              }
            : EMPTY_OBJECT)}
          disabled={isSubmitting}
          error={!!contentError}
          id={fields._id.value}
          initialValue={getIn(initialValues, fields.content.name) as string}
          label="Contenu"
          name={fields.content.name}
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          resolversSettings={documentResolversSettings}
          storage={storage}
          value={fields.content.value}
        />
      </Grid>
    </Grid>
  );
};
